// Dog Pose Detector - Browser Version
// Uses TensorFlow.js (pure JS, no native compilation needed)

import { loadYoloModel, detectPose } from "yolo-helpers/dist/browser";

let tf: typeof import("@tensorflow/tfjs") | null = null;
async function getTf() {
  if (!tf) {
    tf = await import("@tensorflow/tfjs");
  }
  return tf;
}

// Dog keypoint indices (from model metadata)
export const DOG_KEYPOINT_NAMES = [
  "front_left_paw",
  "front_left_knee",
  "front_left_elbow",
  "rear_left_paw",
  "rear_left_knee",
  "rear_left_elbow",
  "front_right_paw",
  "front_right_knee",
  "front_right_elbow",
  "rear_right_paw",
  "rear_right_knee",
  "rear_right_elbow",
  "tail_start",
  "tail_end",
  "left_ear_base",
  "right_ear_base",
  "nose",
  "chin",
  "left_ear_tip",
  "right_ear_tip",
  "left_eye",
  "right_eye",
  "withers",
  "throat",
] as const;

export interface DogKeypoint {
  name: string;
  x: number;
  y: number;
  confidence: number;
}

export interface DogDetection {
  bbox: {
    x: number;
    y: number;
    width: number;
    height: number;
    confidence: number;
  };
  keypoints: DogKeypoint[];
  headCenter: { x: number; y: number } | null;
  tailBase: { x: number; y: number } | null;
  facing: {
    angle: number;
    angleDegrees: number;
    direction: string;
    confidence: number;
  } | null;
  isValid: boolean;
}

export interface DetectionOptions {
  scoreThreshold?: number;
  iouThreshold?: number;
  minKeypointConfidence?: number;
}

const DEFAULT_OPTIONS: Required<DetectionOptions> = {
  scoreThreshold: 0.5,
  iouThreshold: 0.5,
  minKeypointConfidence: 0.3,
};

export class DogDetector {
  private model: any = null;
  private modelPath: string;
  private options: Required<DetectionOptions>;
  private loadPromise: Promise<void> | null = null;

  constructor(modelPath: string, options: DetectionOptions = {}) {
    this.modelPath = modelPath;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  async load(): Promise<void> {
    if (this.model) return;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      console.log("[DogDetector] Loading TensorFlow.js...");
      const tf = await getTf();
      await tf.ready();

      // Use WebGL backend if available, otherwise CPU
      const backend = tf.getBackend();
      console.log("[DogDetector] TensorFlow backend:", backend);

      // Construct full URL if relative path
      let modelUrl = this.modelPath;
      if (!modelUrl.includes("://")) {
        // Use window.location.origin to construct full URL
        const origin = window.location.origin;
        if (!modelUrl.startsWith("/")) {
          modelUrl = "/" + modelUrl;
        }
        modelUrl = origin + modelUrl;
      }

      console.log("[DogDetector] Loading model from:", modelUrl);
      this.model = await loadYoloModel(modelUrl);

      console.log("[DogDetector] Model loaded successfully");
    })();

    return this.loadPromise;
  }

  isLoaded(): boolean {
    return this.model !== null;
  }

  setScoreThreshold(threshold: number): void {
    this.options.scoreThreshold = threshold;
  }

  setIouThreshold(threshold: number): void {
    this.options.iouThreshold = threshold;
  }

  setMinKeypointConfidence(confidence: number): void {
    this.options.minKeypointConfidence = confidence;
  }

  async detectFromImage(
    image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
  ): Promise<DogDetection | null> {
    if (!this.model) {
      await this.load();
    }

    const startTime = performance.now();

    // Run YOLO pose detection
    const predictions = await detectPose({
      tf: await getTf(),
      model: this.model,
      pixels: image as any,
      maxOutputSize: 1,
      num_classes: 1,
      num_keypoints: 24,
      visibility: true,
      scoreThreshold: this.options.scoreThreshold,
      iouThreshold: this.options.iouThreshold,
    });

    const inferenceTime = performance.now() - startTime;
    console.log(`[DogDetector] Inference time: ${inferenceTime.toFixed(1)}ms`);

    // predictions is [batch][detections]
    const batch = predictions[0];
    if (!batch || batch.length === 0) {
      console.log("[DogDetector] No dog detected");
      return null;
    }

    const detection = batch[0];
    const kptCount = detection.keypoints?.length || 0;
    console.log(
      `[DogDetector] Detected dog with ${kptCount} keypoints, confidence: ${(detection.confidence * 100).toFixed(1)}%`,
    );

    // Map keypoints to named structure
    const keypoints: DogKeypoint[] = (detection.keypoints || []).map(
      (kp: any, index: number) => ({
        name: DOG_KEYPOINT_NAMES[index] || `keypoint_${index}`,
        x: kp.x,
        y: kp.y,
        confidence: kp.visibility,
      }),
    );

    // Log all keypoints with confidence
    console.log(
      "[DogDetector] All keypoints:",
      keypoints
        .map((kp) => `${kp.name}: ${kp.confidence.toFixed(2)}`)
        .join(", "),
    );

    // Calculate facing from head-tail
    const facing = this.calculateFacing(keypoints);

    const result: DogDetection = {
      bbox: {
        x: detection.x,
        y: detection.y,
        width: detection.width,
        height: detection.height,
        confidence: detection.confidence,
      },
      keypoints,
      headCenter: facing?.headCenter || null,
      tailBase: facing?.tailBase || null,
      facing: facing
        ? {
            angle: facing.angle,
            angleDegrees: facing.angleDegrees,
            direction: facing.direction,
            confidence: facing.confidence,
          }
        : null,
      isValid: facing !== null,
    };

    return result;
  }

  async detectFromUrl(imageUrl: string): Promise<DogDetection | null> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Only set crossOrigin for non-data URLs
      if (!imageUrl.startsWith("data:")) {
        img.crossOrigin = "anonymous";
      }
      img.onload = async () => {
        try {
          const result = await this.detectFromImage(img);
          resolve(result);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () =>
        reject(
          new Error(`Failed to load image: ${imageUrl.substring(0, 100)}...`),
        );
      img.src = imageUrl;
    });
  }

  private calculateFacing(
    keypoints: DogKeypoint[],
  ):
    | (DogDetection["facing"] & {
        headCenter: { x: number; y: number };
        tailBase: { x: number; y: number };
      })
    | null {
    const leftEarBase = keypoints.find((kp) => kp.name === "left_ear_base");
    const rightEarBase = keypoints.find((kp) => kp.name === "right_ear_base");
    const tailStart = keypoints.find((kp) => kp.name === "tail_start");

    // Log keypoint values
    console.log("[DogDetector] Keypoints for facing:", {
      leftEarBase: leftEarBase
        ? {
            x: leftEarBase.x.toFixed(1),
            y: leftEarBase.y.toFixed(1),
            conf: leftEarBase.confidence.toFixed(2),
          }
        : null,
      rightEarBase: rightEarBase
        ? {
            x: rightEarBase.x.toFixed(1),
            y: rightEarBase.y.toFixed(1),
            conf: rightEarBase.confidence.toFixed(2),
          }
        : null,
      tailStart: tailStart
        ? {
            x: tailStart.x.toFixed(1),
            y: tailStart.y.toFixed(1),
            conf: tailStart.confidence.toFixed(2),
          }
        : null,
    });

    // Check if we have valid keypoints
    const minConf = this.options.minKeypointConfidence;
    if (
      !leftEarBase ||
      leftEarBase.confidence < minConf ||
      !rightEarBase ||
      rightEarBase.confidence < minConf ||
      !tailStart ||
      tailStart.confidence < minConf
    ) {
      console.log(
        "[DogDetector] Insufficient keypoints for facing calculation",
        {
          minConf,
          leftEarValid: leftEarBase && leftEarBase.confidence >= minConf,
          rightEarValid: rightEarBase && rightEarBase.confidence >= minConf,
          tailValid: tailStart && tailStart.confidence >= minConf,
        },
      );
      return null;
    }

    // Calculate head center (midpoint between ear bases)
    const headCenter = {
      x: (leftEarBase.x + rightEarBase.x) / 2,
      y: (leftEarBase.y + rightEarBase.y) / 2,
    };

    const tailBase = {
      x: tailStart.x,
      y: tailStart.y,
    };

    // Vector from tail to head (dog faces away from tail)
    const dx = headCenter.x - tailBase.x;
    const dy = headCenter.y - tailBase.y;

    // Angle in radians (0 = right, PI/2 = down, PI = left, -PI/2 = up)
    // Note: In image coordinates, Y increases downward
    let angle = Math.atan2(dy, dx);
    angle = this.normalizeAngle(angle);

    const angleDegrees = angle * (180 / Math.PI);
    const direction = this.angleToDirection(angle);
    const confidence =
      (leftEarBase.confidence +
        rightEarBase.confidence +
        tailStart.confidence) /
      3;

    return {
      angle,
      angleDegrees,
      direction,
      confidence,
      headCenter,
      tailBase,
    };
  }

  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= 2 * Math.PI;
    while (angle < -Math.PI) angle += 2 * Math.PI;
    return angle;
  }

  private angleToDirection(angle: number): string {
    const degrees = this.normalizeAngle(angle) * (180 / Math.PI);

    if (degrees >= -22.5 && degrees < 22.5) return "right";
    if (degrees >= 22.5 && degrees < 67.5) return "down-right";
    if (degrees >= 67.5 && degrees < 112.5) return "down";
    if (degrees >= 112.5 && degrees < 157.5) return "down-left";
    if (degrees >= 157.5 || degrees < -157.5) return "left";
    if (degrees >= -157.5 && degrees < -112.5) return "up-left";
    if (degrees >= -112.5 && degrees < -67.5) return "up";
    if (degrees >= -67.5 && degrees < -22.5) return "up-right";
    return "right";
  }

  dispose(): void {
    if (this.model) {
      this.model.dispose?.();
      this.model = null;
    }
    this.loadPromise = null;
  }
}

// Singleton instance
let detectorInstance: DogDetector | null = null;

export function getDogDetector(modelPath?: string): DogDetector {
  if (!detectorInstance) {
    const defaultPath =
      modelPath || "/models/dog-pose-50-epoch-best-yolo11_web_model/model.json";
    detectorInstance = new DogDetector(defaultPath);
  }
  return detectorInstance;
}

export function resetDogDetector(): void {
  if (detectorInstance) {
    detectorInstance.dispose();
  }
  detectorInstance = null;
}

// Utility function to draw detection on canvas
export function drawDetectionOnCanvas(
  ctx: CanvasRenderingContext2D,
  detection: DogDetection,
  options: {
    drawBbox?: boolean;
    drawKeypoints?: boolean;
    drawFacing?: boolean;
    keypointNames?: string[];
    scaleX?: number;
    scaleY?: number;
    simpleMode?: boolean;
  } = {},
): void {
  const {
    drawBbox = true,
    drawKeypoints = false,
    drawFacing = true,
    keypointNames = ["left_ear_base", "right_ear_base", "tail_start", "nose"],
    scaleX = 1,
    scaleY = 1,
    simpleMode = true,
  } = options;

  const { bbox, keypoints, facing } = detection;

  // Scale coordinates from model space (640x640) to image space
  // YOLO stretches image to square, so X and Y scale independently
  // scaleX = imageWidth / 640, scaleY = imageHeight / 640

  // Draw bounding box (always in simple mode, controlled by drawBbox in detailed mode)
  if (drawBbox) {
    // YOLO bbox: center_x, center_y, width, height (in model space)
    const scaledX = bbox.x * scaleX;
    const scaledY = bbox.y * scaleY;
    const scaledWidth = bbox.width * scaleX;
    const scaledHeight = bbox.height * scaleY;

    // Convert to top-left corner
    const boxX = scaledX - scaledWidth / 2;
    const boxY = scaledY - scaledHeight / 2;

    ctx.strokeStyle = "lime";
    ctx.lineWidth = 2;
    ctx.strokeRect(boxX, boxY, scaledWidth, scaledHeight);

    // Draw confidence label
    ctx.fillStyle = "lime";
    ctx.font = "14px Arial";
    ctx.fillText(
      `dog ${(bbox.confidence * 100).toFixed(0)}%`,
      boxX + 5,
      boxY - 5,
    );
  }

  // In simple mode, only draw head center and tail start with connecting line
  if (simpleMode) {
    if (!detection.headCenter || !detection.tailBase) {
      console.log(
        "[DogDetector] Cannot draw simple mode: headCenter or tailBase missing",
      );
      return;
    }

    const scaledHeadX = detection.headCenter.x * scaleX;
    const scaledHeadY = detection.headCenter.y * scaleY;
    const scaledTailX = detection.tailBase.x * scaleX;
    const scaledTailY = detection.tailBase.y * scaleY;

    console.log("[DogDetector] Drawing simple mode:", {
      scaledHeadX,
      scaledHeadY,
      scaledTailX,
      scaledTailY,
    });

    // Draw line from tail to head
    ctx.beginPath();
    ctx.moveTo(scaledTailX, scaledTailY);
    ctx.lineTo(scaledHeadX, scaledHeadY);
    ctx.strokeStyle = "rgba(255, 255, 0, 0.8)";
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw tail start point (green)
    ctx.beginPath();
    ctx.arc(scaledTailX, scaledTailY, 8, 0, 2 * Math.PI);
    ctx.fillStyle = "rgba(0, 255, 0, 0.9)";
    ctx.fill();
    ctx.strokeStyle = "white";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw head center point (cyan)
    ctx.beginPath();
    ctx.arc(scaledHeadX, scaledHeadY, 8, 0, 2 * Math.PI);
    ctx.fillStyle = "rgba(0, 255, 255, 0.9)";
    ctx.fill();
    ctx.strokeStyle = "white";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw labels
    ctx.font = "bold 11px Arial";
    ctx.fillStyle = "white";
    ctx.fillText("Head", scaledHeadX + 12, scaledHeadY + 4);
    ctx.fillText("Tail", scaledTailX + 12, scaledTailY + 4);

    // Draw direction label if facing info available
    if (facing) {
      ctx.fillStyle = "yellow";
      ctx.font = "bold 12px Arial";
      ctx.fillText(facing.direction, scaledHeadX + 12, scaledHeadY - 10);
    }

    return;
  }

  // Draw keypoints (non-simple mode)
  if (drawKeypoints) {
    const keypointColors: Record<string, string> = {
      left_ear_base: "#FF0000",
      right_ear_base: "#FF0000",
      tail_start: "#00FF00",
      tail_end: "#00FF00",
      nose: "#FFFF00",
      chin: "#FFFF00",
    };

    for (const kp of keypoints) {
      const color = keypointColors[kp.name] || "#00FFFF";
      const radius = keypointNames.includes(kp.name) ? 5 : 3;
      const showLabel = keypointNames.includes(kp.name) && kp.confidence >= 0.5;

      if (kp.confidence >= 0.3) {
        // Scale coordinates
        const scaledX = kp.x * scaleX;
        const scaledY = kp.y * scaleY;

        ctx.beginPath();
        ctx.arc(scaledX, scaledY, radius, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1;
        ctx.stroke();

        if (showLabel) {
          ctx.fillStyle = color;
          ctx.font = "10px Arial";
          ctx.fillText(kp.name, scaledX + 8, scaledY + 4);
        }
      }
    }
  }

  // Draw facing direction arrow (non-simple mode)
  if (drawFacing && facing && detection.headCenter && detection.tailBase) {
    const { headCenter, tailBase } = detection;

    // Scale coordinates
    const scaledHeadX = headCenter.x * scaleX;
    const scaledHeadY = headCenter.y * scaleY;
    const scaledTailX = tailBase.x * scaleX;
    const scaledTailY = tailBase.y * scaleY;

    // Draw line from tail to head
    ctx.beginPath();
    ctx.moveTo(scaledTailX, scaledTailY);
    ctx.lineTo(scaledHeadX, scaledHeadY);
    ctx.strokeStyle = "yellow";
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw arrow head
    const angle = Math.atan2(
      scaledHeadY - scaledTailY,
      scaledHeadX - scaledTailX,
    );
    const arrowSize = 10;
    const arrowX1 = scaledHeadX - arrowSize * Math.cos(angle - Math.PI / 6);
    const arrowY1 = scaledHeadY - arrowSize * Math.sin(angle - Math.PI / 6);
    const arrowX2 = scaledHeadX - arrowSize * Math.cos(angle + Math.PI / 6);
    const arrowY2 = scaledHeadY - arrowSize * Math.sin(angle + Math.PI / 6);

    ctx.beginPath();
    ctx.moveTo(scaledHeadX, scaledHeadY);
    ctx.lineTo(arrowX1, arrowY1);
    ctx.lineTo(arrowX2, arrowY2);
    ctx.closePath();
    ctx.fillStyle = "yellow";
    ctx.fill();

    // Draw direction label
    ctx.fillStyle = "yellow";
    ctx.font = "bold 12px Arial";
    ctx.fillText(facing.direction, scaledHeadX + 10, scaledHeadY - 10);
  }
}
